import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Brain, Clock, Lock, ShieldCheck, ChevronRight, Loader2, RotateCcw,
  Eye, Trash2, CheckCircle2, History, ArrowLeft,
} from 'lucide-react';
import toast from '../utils/toast';
import DashboardLayout from '../components/Layout/DashboardLayout';
import ConfirmModal from '../components/UI/ConfirmModal';
import { talentAssessmentsAPI } from '../services/api';
import { formatDate } from '../utils/dateUtils';

/**
 * Big Five Profile — intro / status page.
 *
 * This is the single page the Self-Service "Big Five Profile" card opens.
 * It dynamically renders the right CTAs based on the user's state:
 *   - No consent → "Get started" → consent screen
 *   - Consent + no session → "Start the assessment" → take page
 *   - In-progress session → "Resume (X%)" + "Start over"
 *   - Submitted result + retake eligible → "View result" + "Retake"
 *   - Submitted result + retake not yet eligible → "View result" + countdown
 */
const SelfServiceBigFive = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [acting, setActing] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await talentAssessmentsAPI.getBigFiveStatus();
      setStatus(data);
    } catch (err) {
      console.error('Failed to load Big Five status', err);
      toast.error('Could not load your Big Five Profile status. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleStart = async () => {
    if (!status) return;
    if (!status.has_current_consent) {
      navigate('/self-service/big-five/consent');
      return;
    }
    setActing(true);
    try {
      const { data } = await talentAssessmentsAPI.startBigFiveSession({});
      navigate(`/self-service/big-five/take/${data.id}`);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'CONSENT_REQUIRED') {
        navigate('/self-service/big-five/consent');
        return;
      }
      if (code === 'RETAKE_NOT_YET_AVAILABLE') {
        toast.error(err.response?.data?.detail || 'Retake not yet available.');
      } else {
        toast.error('Could not start the assessment. Please try again.');
      }
    } finally {
      setActing(false);
    }
  };

  const handleResume = () => {
    if (status?.in_progress_session?.id) {
      navigate(`/self-service/big-five/take/${status.in_progress_session.id}`);
    }
  };

  const handleStartOver = async () => {
    if (!status?.in_progress_session?.id) return;
    setActing(true);
    try {
      await talentAssessmentsAPI.voidBigFiveSession(status.in_progress_session.id);
      toast.success('Progress cleared.');
      await loadStatus();
    } catch (err) {
      toast.error('Could not clear your in-progress session.');
    } finally {
      setActing(false);
      setConfirmAction(null);
    }
  };

  const handleWithdraw = async () => {
    setActing(true);
    try {
      await talentAssessmentsAPI.withdrawBigFiveConsent('Withdrew via Self-Service');
      toast.success('Consent withdrawn. Your Big Five data has been deleted.');
      await loadStatus();
    } catch (err) {
      toast.error('Could not withdraw consent. Please try again.');
    } finally {
      setActing(false);
      setConfirmAction(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-blue-800 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const inv = status?.inventory || {};
  const inProgress = status?.in_progress_session || null;
  const completed = status?.latest_completed_session || null;
  const retakeAvailable = !!status?.retake_available;
  const daysRemaining = status?.retake_days_remaining;
  const progressPct = inProgress
    ? Math.round((inProgress.answered_count / Math.max(1, inProgress.total_count)) * 100)
    : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen pb-24">
        {/* Hero — single page anchor (title, description, primary actions). */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 sm:p-6 border-b border-blue-100">
            <div className="flex items-start gap-3">
              <Link
                to="/self-service"
                aria-label="Back to Self-Service"
                className="icon-button flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="min-w-0 flex-1">
                <h1 className="text-section-header">{inv.name || 'Big Five Profile'}</h1>
                <p className="text-body mt-1">
                  {inv.description ||
                    'Discover how you score across five core personality dimensions: ' +
                      'Openness, Conscientiousness, Extraversion, Agreeableness, and Neuroticism.'}
                </p>
              </div>
            </div>
          </div>

          <div className="p-5 sm:p-6 space-y-4">
            {/* In-progress: resume + start over */}
            {inProgress && (
              <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-4 sm:p-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-blue-900">
                      You have an in-progress assessment
                    </div>
                    <div className="text-xs text-blue-800 mt-0.5">
                      {inProgress.answered_count} of {inProgress.total_count} questions answered ({progressPct}%)
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmAction('startOver')}
                      disabled={acting}
                      className="btn btn-sm btn-outline"
                    >
                      <RotateCcw className="w-4 h-4" />Start over
                    </button>
                    <button
                      type="button"
                      onClick={handleResume}
                      disabled={acting}
                      className="btn btn-sm btn-primary"
                    >
                      Resume <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {/* Mini progress bar */}
                <div className="mt-3 h-2 bg-white rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-800 transition-all duration-300"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                {/* If a previous result exists, expose it inline so the user
                    isn't locked out of their old score while they retake.
                    The result endpoint always returns the latest *submitted*
                    result regardless of any in-progress session. */}
                {completed && (
                  <div className="mt-3 text-xs">
                    <Link
                      to="/self-service/big-five/result"
                      className="inline-flex items-center text-blue-800 hover:text-blue-900 font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" />
                      View my previous result while I retake
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Completed: view result + retake */}
            {completed && !inProgress && (
              <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4 sm:p-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4" />
                      You completed your last Big Five Profile
                    </div>
                    <div className="text-xs text-emerald-800 mt-0.5">
                      Submitted {formatDate(completed.submitted_at)}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Link
                      to="/self-service/big-five/result"
                      className="btn btn-sm btn-success"
                    >
                      <Eye className="w-4 h-4" />View result
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* What to expect — first-time users only.
                Information-first: answer "what is this and what will it cost me?"
                BEFORE asking for the 15-minute commitment. */}
            {!inProgress && !completed && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Tile icon={Brain} title="Self-insight"
                  body="Understand how you score across the five core personality dimensions." />
                <Tile icon={Clock} title="~15 minutes"
                  body="120 short statements. You can stop and resume anytime." />
                <Tile icon={Lock} title="Confidential"
                  body="Your responses are encrypted. Only you and authorised HR can view individual results." />
              </div>
            )}

            {/* Primary CTA — placed AFTER the value tiles for first-timers; for
                returning users (retake / completed) the resume / view-result
                banners above already serve as the lead action. */}
            {!inProgress && (
              <div>
                {retakeAvailable ? (
                  <button
                    type="button"
                    onClick={handleStart}
                    disabled={acting}
                    className="btn btn-primary w-full sm:w-auto"
                  >
                    {acting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        {completed ? 'Take it again' : 'Start the assessment'}
                        <ChevronRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                ) : (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
                    <Clock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                      <div className="font-semibold">Re-take available in {daysRemaining} days</div>
                      <div className="mt-0.5 text-amber-800 text-xs">
                        Personality is fairly stable, so we ask you to wait at least
                        {' '}{inv.retake_cadence_months || 12} months between sessions
                        for the most meaningful comparison.
                      </div>
                      {status?.retake_available_on && (
                        <div className="mt-1 text-xs text-amber-800">
                          Available from {formatDate(status.retake_available_on)}.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Privacy + governance footer */}
        <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-6">
          <h3 className="text-subsection-header flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-800" />
            Your data, your control
          </h3>
          <ul className="mt-3 space-y-2 text-sm text-gray-700">
            <li className="flex gap-2">
              <span className="text-blue-800">•</span>
              Your individual responses and scores are encrypted at rest in our confidential database.
            </li>
            <li className="flex gap-2">
              <span className="text-blue-800">•</span>
              HR, your HOD, Deputy HOD, and Reporting Officer can view your individual result only with a recorded justification — you can see who, when, and why on your access log.
            </li>
            <li className="flex gap-2">
              <span className="text-blue-800">•</span>
              Aggregated dashboards only show segments with at least 5 respondents, so you cannot be identified from a chart.
            </li>
            <li className="flex gap-2">
              <span className="text-blue-800">•</span>
              You can withdraw consent at any time. We will permanently delete your responses and scores.
            </li>
          </ul>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/self-service/big-five/access-log"
              className="btn btn-sm btn-outline"
            >
              <History className="w-4 h-4" />Who has viewed me
            </Link>
            {status?.has_current_consent && (
              <button
                type="button"
                onClick={() => setConfirmAction('withdraw')}
                disabled={acting}
                className="btn btn-sm btn-danger"
              >
                <Trash2 className="w-4 h-4" />Withdraw &amp; delete data
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Confirm: start over */}
      {confirmAction === 'startOver' && (
        <ConfirmModal
          isOpen
          onCancel={() => setConfirmAction(null)}
          onConfirm={handleStartOver}
          isLoading={acting}
          title="Start over?"
          message="This will discard all your in-progress answers and you will start the Big Five Profile from question 1. Your previous completed result (if any) is unaffected."
          confirmLabel="Yes, start over"
          variant="primary"
          theme="employee"
        />
      )}
      {/* Confirm: withdraw */}
      {confirmAction === 'withdraw' && (
        <ConfirmModal
          isOpen
          onCancel={() => setConfirmAction(null)}
          onConfirm={handleWithdraw}
          isLoading={acting}
          title="Withdraw consent and delete data?"
          message="We will withdraw your consent and permanently delete your Big Five responses and scores. Aggregated company benchmarks are unaffected because they contain no link back to you. This cannot be undone."
          confirmLabel="Yes, delete my data"
          variant="danger"
          theme="employee"
        />
      )}
    </DashboardLayout>
  );
};

const Tile = ({ icon: Icon, title, body }) => (
  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center mb-2">
      <Icon className="w-4.5 h-4.5 text-blue-800" />
    </div>
    <div className="text-sm font-semibold text-gray-900">{title}</div>
    <div className="text-xs text-gray-600 mt-1">{body}</div>
  </div>
);

export default SelfServiceBigFive;
